/**
 * @author Vivek Kumar <vivek.kumar@314ecorp.com>
 * @description Upload manager store to track background file uploads
 */
import _ from 'lodash';
import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';

interface UploadManagerStoreState {
	uploads: UploadManager.ITask[];
	collapsed: boolean;
	actions: UploadManager.IActions;
}

// Client-only correlation id for the upload-manager UI — not security-sensitive, so Math.random is fine here.
// eslint-disable-next-line sonarjs/pseudo-random
const generateUploadId = (): string => `upload-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const createTask = ({
	id,
	fileName,
	size,
	entityId,
	entityType,
	operation,
	status,
}: UploadManager.ITaskPayload & {
	operation: UploadManager.IOperation;
	status: UploadManager.IStatus;
}): UploadManager.ITask => ({
	id: id ?? generateUploadId(),
	fileName,
	size,
	progress: 0,
	status,
	startedAt: new Date().toISOString(),
	entityId,
	entityType,
	operation,
});

const useUploadManagerStore = create<UploadManagerStoreState>((set) => ({
	uploads: [],
	collapsed: true,
	actions: {
		startUpload: ({ id, fileName, size, entityId, entityType }) => {
			const upload = createTask({
				id,
				fileName,
				size,
				entityId,
				entityType,
				operation: 'upload',
				status: 'queued',
			});
			set((state) => ({
				uploads: [...state.uploads, upload],
				collapsed: false,
			}));
			return upload.id;
		},
		startDelete: ({ id, fileName, size, entityId, entityType }) => {
			const upload = createTask({
				id,
				fileName,
				size,
				entityId,
				entityType,
				operation: 'delete',
				status: 'deleting',
			});
			set((state) => ({
				uploads: [...state.uploads, upload],
				collapsed: false,
			}));
			return upload.id;
		},
		updateUpload: (uploadId, update) => {
			set((state) => ({
				uploads: _.map(state.uploads, (upload) => (upload.id === uploadId ? { ...upload, ...update } : upload)),
			}));
		},
		removeUpload: (uploadId) => {
			set((state) => ({ uploads: _.reject(state.uploads, (upload) => upload.id === uploadId) }));
		},
		clearFinished: () => {
			set((state) => ({
				uploads: _.reject(state.uploads, (upload) => upload.status === 'completed' || upload.status === 'failed'),
			}));
		},
		setCollapsed: (collapsed) => set({ collapsed }),
	},
}));

const useUploadManagerValues = (): Pick<UploadManagerStoreState, 'uploads' | 'collapsed'> =>
	useUploadManagerStore(
		useShallow(({ uploads, collapsed }) => ({
			uploads,
			collapsed,
		})),
	);

const useUploadManagerActions = (): UploadManager.IActions => useUploadManagerStore((state) => state.actions);

export { useUploadManagerValues, useUploadManagerActions };
export default useUploadManagerStore;
